import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Shield, Globe, FileText, Image, Bot, Zap, Code2, Type,
  AlertTriangle, CheckCircle2, XCircle, ChevronDown, ChevronUp, ExternalLink,
  BarChart3, BookOpen, FileSearch, Target, Loader2,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';

/* ─── Config ─────────────────────────────────────────────────────────────── */
const SECTION_META = {
  technical_seo:          { icon: Shield,   label: 'Technical SEO' },
  content_quality:        { icon: BookOpen, label: 'Content Quality' },
  on_page_seo:            { icon: Type,     label: 'On-Page SEO' },
  schema_structured_data: { icon: Code2,    label: 'Schema & Structured Data' },
  performance:            { icon: Zap,      label: 'Performance & Core Web Vitals' },
  images:                 { icon: Image,    label: 'Images' },
  ai_search_readiness:    { icon: Bot,      label: 'AI Search Readiness' },
  bing_analytics:         { icon: BarChart3,label: 'Bing Webmaster Analytics' },
  penalty_status:         { icon: AlertTriangle, label: 'Google Penalty Status' },
};

const STATUS_STYLES = {
  good:              { bg: 'bg-emerald-100 dark:bg-emerald-900/30', text: 'text-emerald-700 dark:text-emerald-400', label: 'Good' },
  needs_improvement: { bg: 'bg-amber-100 dark:bg-amber-900/30',    text: 'text-amber-700 dark:text-amber-400',    label: 'Needs Work' },
  poor:              { bg: 'bg-red-100 dark:bg-red-900/30',         text: 'text-red-700 dark:text-red-400',         label: 'Poor' },
};

const GRADE_COLORS = {
  'A+': 'bg-emerald-500', A: 'bg-emerald-500', B: 'bg-green-500',
  C: 'bg-amber-500', D: 'bg-orange-500', F: 'bg-red-500',
};

const SEVERITY_BORDER = {
  critical: 'border-l-red-500',
  high:     'border-l-amber-500',
  medium:   'border-l-blue-500',
  low:      'border-l-slate-400',
};

const SEVERITY_BADGE = {
  critical: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  high:     'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  medium:   'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  low:      'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
};

const EFFORT_BADGE = {
  low:    'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400',
  medium: 'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400',
  high:   'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400',
};

const CWV_THRESHOLDS = {
  lcp:         { good: 2500,  needs: 4000  },
  cls:         { good: 0.1,   needs: 0.25  },
  fcp:         { good: 1800,  needs: 3000  },
  tbt:         { good: 200,   needs: 600   },
  speed_index: { good: 3400,  needs: 5800  },
  tti:         { good: 3800,  needs: 7300  },
};

const CWV_LABELS = {
  lcp: 'Largest Contentful Paint', cls: 'Cumulative Layout Shift',
  fcp: 'First Contentful Paint', tbt: 'Total Blocking Time',
  speed_index: 'Speed Index', tti: 'Time to Interactive',
};

const fadeUp = (d = 0) => ({
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.3, delay: d },
});

const barColor = (pct) => pct >= 80 ? 'bg-emerald-500' : pct >= 50 ? 'bg-amber-500' : 'bg-red-500';

const cwvColor = (key, value) => {
  const t = CWV_THRESHOLDS[key];
  if (!t || value == null) return 'text-slate-500';
  return value <= t.good ? 'text-emerald-600 dark:text-emerald-400'
       : value <= t.needs ? 'text-amber-600 dark:text-amber-400'
       : 'text-red-600 dark:text-red-400';
};

const formatDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }); }
  catch { return d; }
};

const renderValue = (v) => {
  if (v === true) return <CheckCircle2 className="w-4 h-4 text-emerald-500" />;
  if (v === false) return <XCircle className="w-4 h-4 text-red-400" />;
  if (v == null) return <span className="text-slate-400">—</span>;
  if (Array.isArray(v)) return v.length ? v.join(', ') : '—';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
};

const humanize = (s) => s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

/* ─── Sub-components ─────────────────────────────────────────────────────── */

const ExecutiveSummary = ({ data }) => {
  const { overall_score, grade, domain, business_type, scan_date, score_summary, top_critical_issues = [], top_quick_wins = [], critical_count, high_count, total_recommendations, target_keyword, url } = data;

  return (
    <motion.div {...fadeUp(0.05)}>
      <div className="rounded-2xl bg-gradient-to-br from-indigo-600 via-indigo-700 to-violet-700 p-6 md:p-8 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(255,255,255,0.1),transparent_60%)]" />
        <div className="relative z-10">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
            <div className="space-y-3 flex-1">
              <div className="flex items-center gap-3">
                <Globe className="w-5 h-5 text-indigo-200" />
                <h2 className="font-heading text-xl font-bold">{domain || 'SEO Audit Report'}</h2>
                {url && (
                  <a href={url} target="_blank" rel="noopener noreferrer" className="text-indigo-200 hover:text-white transition-colors">
                    <ExternalLink className="w-4 h-4" />
                  </a>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2 text-sm text-indigo-200">
                {business_type && <Badge className="bg-white/15 text-white border-white/20 text-xs">{humanize(business_type)}</Badge>}
                {target_keyword && <Badge className="bg-white/15 text-white border-white/20 text-xs"><Target className="w-3 h-3 mr-1" />{target_keyword}</Badge>}
                <span>{formatDate(scan_date)}</span>
              </div>
              <p className="text-sm text-indigo-100 leading-relaxed max-w-xl">{score_summary}</p>
              <div className="flex items-center gap-4 text-xs text-indigo-200 pt-1">
                <span>{total_recommendations} recommendations</span>
                {critical_count > 0 && <span className="text-red-300">{critical_count} critical</span>}
                {high_count > 0 && <span className="text-amber-300">{high_count} high priority</span>}
              </div>
            </div>
            <div className="flex flex-col items-center gap-2">
              <div className="text-6xl font-bold font-heading tracking-tight">{overall_score}</div>
              <span className={`px-3 py-1 rounded-full text-sm font-bold ${GRADE_COLORS[grade] || 'bg-slate-500'} text-white`}>
                Grade {grade}
              </span>
              <span className="text-xs text-indigo-200">out of 100</span>
            </div>
          </div>

          {(top_critical_issues.length > 0 || top_quick_wins.length > 0) && (
            <div className="grid md:grid-cols-2 gap-4 mt-6">
              {top_critical_issues.length > 0 && (
                <div className="bg-red-500/15 border border-red-400/20 rounded-xl p-4 space-y-2">
                  <h3 className="text-sm font-semibold flex items-center gap-2 text-red-200">
                    <AlertTriangle className="w-4 h-4" /> Top Critical Issues
                  </h3>
                  {top_critical_issues.map((item, i) => (
                    <div key={i} className="text-xs text-red-100">
                      <span className="font-medium">{item.title}</span>
                      {item.description && <span className="text-red-200/80 ml-1">— {item.description}</span>}
                    </div>
                  ))}
                </div>
              )}
              {top_quick_wins.length > 0 && (
                <div className="bg-emerald-500/15 border border-emerald-400/20 rounded-xl p-4 space-y-2">
                  <h3 className="text-sm font-semibold flex items-center gap-2 text-emerald-200">
                    <Zap className="w-4 h-4" /> Top Quick Wins
                  </h3>
                  {top_quick_wins.map((item, i) => (
                    <div key={i} className="text-xs text-emerald-100">
                      <span className="font-medium">{item.title}</span>
                      {item.estimated_impact && <span className="text-emerald-200/80 ml-1">— {item.estimated_impact}</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

const ScoringBreakdown = ({ breakdown }) => {
  const entries = Object.entries(breakdown || {});
  if (!entries.length) return null;

  return (
    <motion.div {...fadeUp(0.1)}>
      <Card className="gds-card">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-indigo-600" /> Scoring Breakdown
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2.5">
          {entries.map(([key, item]) => {
            const pct = item.max > 0 ? Math.round((item.score / item.max) * 100) : 0;
            return (
              <div key={key} className="flex items-center gap-3">
                <span className="text-sm font-medium text-slate-700 dark:text-slate-300 w-40 truncate" title={item.label}>
                  {item.label || humanize(key)}
                </span>
                <div className="flex-1 h-2.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                  <motion.div
                    className={`h-full rounded-full ${barColor(pct)}`}
                    initial={{ width: 0 }}
                    animate={{ width: `${pct}%` }}
                    transition={{ duration: 0.6, ease: 'easeOut' }}
                  />
                </div>
                <span className="text-xs font-mono text-slate-500 w-14 text-right">{item.score}/{item.max}</span>
                {item.issues_count > 0 && (
                  <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-800">
                    {item.issues_count} {item.issues_count === 1 ? 'issue' : 'issues'}
                  </Badge>
                )}
              </div>
            );
          })}
        </CardContent>
      </Card>
    </motion.div>
  );
};

const SectionCard = ({ sectionKey, section }) => {
  const [expanded, setExpanded] = useState(false);
  const meta = SECTION_META[sectionKey];
  if (!meta) return null;

  const Icon = meta.icon;
  const status = STATUS_STYLES[section.status] || STATUS_STYLES.needs_improvement;
  const score = section.score;
  const max = section.max;
  const findings = section.findings || {};
  const issues = section.issues || [];
  const checks = section.checks || {};

  return (
    <Card className="gds-card overflow-hidden">
      <div
        className="flex items-center justify-between p-4 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 rounded-lg bg-indigo-50 dark:bg-indigo-900/30 flex items-center justify-center flex-shrink-0">
            <Icon className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
          </div>
          <h3 className="font-heading font-semibold text-sm truncate">{meta.label}</h3>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          <Badge className={`${status.bg} ${status.text} border-0 text-xs`}>{status.label}</Badge>
          {score != null && max != null && (
            <span className="text-xs font-mono text-slate-500">{score}/{max}</span>
          )}
          {expanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
        </div>
      </div>
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 space-y-4 border-t border-slate-100 dark:border-slate-800 pt-4">
              {Object.keys(findings).length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Findings</p>
                  <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5">
                    {Object.entries(findings).map(([k, v]) => (
                      <div key={k} className="flex items-center justify-between text-sm py-1 border-b border-slate-50 dark:border-slate-800/50">
                        <span className="text-slate-500">{humanize(k)}</span>
                        <span className="font-medium text-slate-700 dark:text-slate-300 flex items-center gap-1">{renderValue(v)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {issues.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Issues ({issues.length})</p>
                  <div className="space-y-1.5">
                    {issues.map((issue, i) => (
                      <div key={i} className="flex items-start gap-2 text-sm">
                        <AlertTriangle className="w-3.5 h-3.5 text-amber-500 mt-0.5 flex-shrink-0" />
                        <span className="text-slate-600 dark:text-slate-400">{typeof issue === 'string' ? issue : issue.message || issue.title || JSON.stringify(issue)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {Object.keys(checks).length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Checks</p>
                  <div className="grid sm:grid-cols-2 gap-1.5">
                    {Object.entries(checks).map(([k, v]) => {
                      const passed = v === true || v === 'pass';
                      return (
                        <div key={k} className="flex items-center gap-2 text-sm py-1">
                          {passed
                            ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                            : <XCircle className="w-3.5 h-3.5 text-red-400 flex-shrink-0" />}
                          <span className="text-slate-600 dark:text-slate-400">{humanize(k)}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </Card>
  );
};

const PerformanceMetrics = ({ perf }) => {
  if (!perf?.available) return null;

  const metrics = perf.metrics || {};
  const entries = Object.entries(metrics).filter(([, v]) => v?.value != null);
  if (!entries.length) return null;

  return (
    <motion.div {...fadeUp(0.2)}>
      <Card className="gds-card">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="font-heading flex items-center gap-2">
              <Zap className="w-5 h-5 text-amber-500" /> Core Web Vitals
            </CardTitle>
            {perf.performance_score != null && (
              <Badge className={`text-xs border-0 ${perf.performance_score >= 90 ? 'bg-emerald-100 text-emerald-700' : perf.performance_score >= 50 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                Performance Score: {perf.performance_score}
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {entries.map(([key, metric]) => (
              <div key={key} className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-4 text-center">
                <p className="text-xs text-slate-500 font-medium uppercase tracking-wide mb-1">{CWV_LABELS[key] || humanize(key)}</p>
                <p className={`text-2xl font-bold font-heading ${cwvColor(key, metric.value)}`}>
                  {metric.display || metric.value}
                </p>
                {metric.score != null && (
                  <p className="text-xs text-slate-400 mt-1">Score: {metric.score}</p>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

const ActionPlan = ({ plan }) => {
  const groups = [
    { key: 'critical',   label: 'Critical',   severity: 'critical' },
    { key: 'high',       label: 'High',       severity: 'high' },
    { key: 'medium',     label: 'Medium',     severity: 'medium' },
    { key: 'low',        label: 'Low',        severity: 'low' },
    { key: 'quick_wins', label: 'Quick Wins', severity: 'low' },
  ];

  const hasItems = groups.some((g) => (plan[g.key] || []).length > 0);
  if (!hasItems) return null;

  return (
    <motion.div {...fadeUp(0.3)}>
      <Card className="gds-card">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading flex items-center gap-2">
            <Target className="w-5 h-5 text-indigo-600" /> Action Plan
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {groups.map(({ key, label, severity }) => {
            const items = plan[key] || [];
            if (!items.length) return null;
            return (
              <div key={key}>
                <div className="flex items-center gap-2 mb-3">
                  <Badge className={`${SEVERITY_BADGE[severity]} border-0 text-xs`}>{label}</Badge>
                  <span className="text-xs text-slate-400">{items.length} {items.length === 1 ? 'item' : 'items'}</span>
                </div>
                <div className="space-y-2">
                  {items.map((item, i) => (
                    <ActionItem key={i} item={item} severity={severity} />
                  ))}
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>
    </motion.div>
  );
};

const ActionItem = ({ item, severity }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className={`border-l-4 ${SEVERITY_BORDER[severity] || 'border-l-slate-300'} border border-slate-200 dark:border-slate-700 rounded-r-xl bg-white dark:bg-slate-900 overflow-hidden`}>
      <div
        className="p-3 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">{item.title}</p>
            <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">{item.description}</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {item.category && <Badge variant="outline" className="text-[10px]">{humanize(item.category)}</Badge>}
            {item.effort && <Badge className={`${EFFORT_BADGE[item.effort] || EFFORT_BADGE.medium} border-0 text-[10px]`}>{item.effort} effort</Badge>}
            {expanded ? <ChevronUp className="w-3.5 h-3.5 text-slate-400" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400" />}
          </div>
        </div>
      </div>
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-3 pb-3 space-y-2 border-t border-slate-100 dark:border-slate-800 pt-3 text-sm">
              {item.description && <p className="text-slate-600 dark:text-slate-400">{item.description}</p>}
              {item.recommended_fix && (
                <div>
                  <p className="text-xs font-semibold text-slate-500 mb-1">Recommended Fix</p>
                  <p className="text-slate-700 dark:text-slate-300">{item.recommended_fix}</p>
                </div>
              )}
              {item.code_snippet && (
                <pre className="bg-slate-900 dark:bg-slate-950 text-slate-100 text-xs rounded-lg p-3 overflow-x-auto whitespace-pre-wrap">
                  {item.code_snippet}
                </pre>
              )}
              {item.estimated_impact && (
                <p className="text-xs text-indigo-600 dark:text-indigo-400 font-medium">Impact: {item.estimated_impact}</p>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/* ─── Main Component ─────────────────────────────────────────────────────── */

export default function SEOAuditReport({ report, loading }) {
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-3">
        <Loader2 className="w-8 h-8 text-indigo-500 animate-spin" />
        <p className="text-sm text-slate-500">Generating audit report…</p>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-4 text-center">
        <div className="w-16 h-16 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
          <FileSearch className="w-8 h-8 text-slate-400" />
        </div>
        <div>
          <h3 className="font-heading font-semibold text-slate-700 dark:text-slate-300">No Report Available</h3>
          <p className="text-sm text-slate-500 mt-1 max-w-sm">
            Run an On-Page SEO scan first to generate a detailed audit report.
          </p>
        </div>
      </div>
    );
  }

  const { executive_summary, sections = {}, action_plan = {}, scoring_breakdown } = report;
  const sectionEntries = Object.entries(sections);
  const perfSection = sections.performance;

  return (
    <div className="space-y-6">
      {executive_summary && <ExecutiveSummary data={executive_summary} />}

      {scoring_breakdown && <ScoringBreakdown breakdown={scoring_breakdown} />}

      {sectionEntries.length > 0 && (
        <motion.div {...fadeUp(0.15)} className="space-y-3">
          <h2 className="font-heading font-semibold text-lg flex items-center gap-2">
            <FileText className="w-5 h-5 text-indigo-600" /> Detailed Analysis
          </h2>
          {sectionEntries.map(([key, section]) => (
            <SectionCard key={key} sectionKey={key} section={section} />
          ))}
        </motion.div>
      )}

      {perfSection && <PerformanceMetrics perf={perfSection} />}

      <ActionPlan plan={action_plan} />
    </div>
  );
}
